import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { BadgeCounter } from '@components/badge-counter';
import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { Header } from '@components/header';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconEdit } from '@components/icons/edit';
import { IconPersonLimited } from '@components/icons/person-limited';
import { IconRemove } from '@components/icons/remove';
import { IconTeam } from '@components/icons/team';
import { InfoBox } from '@components/info-box';
import { Spinner } from '@components/loading-spinner';
import { PageContent } from '@components/page-containers/page-container';
import { ContentLeft, ContentRight, Grid } from '@components/page-containers/page-containers';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { UserCard } from '@components/user-card/user-card';
import { useScopedTranslate } from '@locale/translate';
import { getTranslatedTitle } from '@utils/translated-title';

import { useGetActiveFollowers, useGetJob, useUpdateFollowers } from 'api/jobs/hooks';
import { ApplicationFollower, Follower, FollowerResponse } from 'api/jobs/requests';
import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';
import { Job } from 'types/jobs';

import { useAddToHiringTeamSidebar } from './add-to-hiring-team';

import { Actions } from '../top-bar-actions';
import { useUnsavedChangesPrompt } from '../unsaved-changes-modal';

export const Access = () => {
  const { jobId } = useParams();
  const followerQuery = useGetActiveFollowers(Number(jobId));
  const jobQuery = useGetJob(Number(jobId));

  if (!followerQuery.data || !jobQuery.data) {
    return <Spinner />;
  }
  return <RenderAccess followerData={followerQuery.data} job={jobQuery.data} />;
};

export const RenderAccess = ({
  followerData,
  job,
}: {
  followerData: FollowerResponse;
  job: Job;
}) => {
  const { jobId } = useParams();
  const save = useUpdateFollowers();
  const [followers, setFollowers] = useState<Follower[]>(followerData.followers ?? []);
  const [appFollowers, setAppFollowers] = useState<ApplicationFollower[]>(
    followerData.appFollowers ?? [],
  );
  const appendFollower = (follower: Follower) =>
    setFollowers((prevState) => [...prevState, follower]);
  const removeFollower = (userId: number) =>
    setFollowers(followers.filter((follower) => follower.id !== userId));
  const appendAppFollower = (follower: ApplicationFollower) =>
    setAppFollowers((prevState) => [...prevState, follower]);
  const removeAppFollower = (userId: number) =>
    setAppFollowers(appFollowers.filter((follower) => follower.id !== userId));

  const isDirty =
    followerData.followers !== followers || followerData.appFollowers !== appFollowers;

  const combinedFollowerIds = [...followers, ...appFollowers].map((follower) => follower.id);

  const handleSave = async () => {
    await save.mutateAsync({
      jobId: Number(jobId),
      followerIds: followers.map((follower) => follower.id),
      appFollowerIds: appFollowers.map((follower) => follower.id),
    });
  };

  useEffect(() => {
    setFollowers(followerData.followers);
    setAppFollowers(followerData.appFollowers);
  }, [followerData.followers, followerData.appFollowers]);

  useUnsavedChangesPrompt({
    isDirty,
    onSave: async () => {
      await handleSave();
    },
    isPublished: job.isPublished,
  });

  const discardAllChanges = () => {
    setFollowers(followerData.followers);
    setAppFollowers(followerData.appFollowers);
  };

  return (
    <>
      <Header
        breadcrumbs={<Breadcrumb items={[{ label: job.title }]} />}
        actions={
          <Actions
            hasUnsavedChanges={isDirty}
            onSave={handleSave}
            job={job}
            onCancel={discardAllChanges}
            dataCyPublish="publish-job"
          />
        }
      />
      <PageContent>
        <Grid>
          <ContentLeft>
            <HiringTeam
              followers={followers}
              combinedFollowerIds={combinedFollowerIds}
              appendFollower={appendFollower}
              removeFollower={removeFollower}
            />
          </ContentLeft>
          <ContentRight>
            <LimitedAccess
              appFollowers={appFollowers}
              combinedFollowerIds={combinedFollowerIds}
              appendAppFollower={appendAppFollower}
              removeAppFollower={removeAppFollower}
              job={job}
            />
          </ContentRight>
        </Grid>
      </PageContent>
    </>
  );
};

export const HiringTeam = ({
  followers,
  combinedFollowerIds,
  appendFollower,
  removeFollower,
  hasManageAccess = true,
}: {
  followers: Follower[];
  combinedFollowerIds: number[];
  appendFollower: (user: Follower) => void;
  removeFollower: (userId: number) => void;
  hasManageAccess?: boolean;
}) => {
  const { t, lang } = useScopedTranslate(accessLocale);
  const showAddToHiringTeamSidebar = useAddToHiringTeamSidebar(
    combinedFollowerIds,
    t('sidebarHeaderHiringTeam'),
    appendFollower,
  );
  const { current } = useStore();

  return (
    <Stack space={6}>
      <Stack orientation="horizontal" space={4} align="center" justify="space-between">
        <Stack orientation="horizontal" space={2} align="center">
          <IconTeam color="purple100" />
          <Text font="bold18" text={t('hiringTeam')} />
        </Stack>
        {hasManageAccess && (
          <Button
            variant="secondary"
            text={t('addToTeam')}
            iconLeft={<IconAddPlus />}
            onClick={() => showAddToHiringTeamSidebar()}
            dataCy="add-to-hiring-team-btn"
          />
        )}
      </Stack>
      <Text font="regular14" color="purple80" text={t('description')} />
      <Separator />
      {followers.length < 1 ? (
        <Stack space={4}>
          <Card variant="gray" space={6}>
            <Stack space={4} orientation="horizontal">
              <IconTeam color="purple80" />
              <Text text={t('noUsersHiring')} color="purple80" />
            </Stack>
          </Card>
          <InfoBox text={t('noteHiring')} variant="indigo" />
        </Stack>
      ) : (
        <Stack space={2}>
          {followers.map((follower) => (
            <UserCard
              key={follower.id}
              {...follower}
              departments={follower.departments?.map(
                (department) =>
                  getTranslatedTitle(department.translations, lang, current.defaultLanguage) ?? '',
              )}
              actions={
                hasManageAccess ? (
                  <IconButton
                    variant="ghost-purple"
                    icon={<IconRemove />}
                    onClick={() => removeFollower(follower.id)}
                    tooltip={t('removeFromHiring')}
                  />
                ) : null
              }
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export const LimitedAccess = ({
  appFollowers,
  combinedFollowerIds,
  appendAppFollower,
  removeAppFollower,
  hasManageAccess = true,
  job,
}: {
  appFollowers: ApplicationFollower[];
  combinedFollowerIds: number[];
  appendAppFollower: (user: ApplicationFollower) => void;
  removeAppFollower: (userId: number) => void;
  hasManageAccess?: boolean;
  job: Job;
}) => {
  const { t, lang } = useScopedTranslate(accessLocale);
  const { showModal } = useModal();
  const { current } = useStore();
  const showAddToHiringTeamSidebar = useAddToHiringTeamSidebar(
    combinedFollowerIds,
    t('sidebarHeaderLimitedAccess'),
    appendAppFollower,
  );

  const editCandidateAccess = (user: ApplicationFollower) => {
    showModal('EDIT_CANDIDATE_ACCESS', {
      jobId: job.id,
      userId: user.id,
      userName: user.name,
    });
  };

  return (
    <Stack space={6}>
      <Stack orientation="horizontal" space={4} align="center" justify="space-between">
        <Stack orientation="horizontal" space={2} align="center">
          <IconPersonLimited color="purple100" />
          <Text font="bold18" text={t('limitedAccess')} />
        </Stack>
        {hasManageAccess && (
          <Button
            variant="secondary"
            text={t('grantAccess')}
            iconLeft={<IconAddPlus />}
            onClick={() => showAddToHiringTeamSidebar()}
            dataCy="add-to-limited-btn"
          />
        )}
      </Stack>
      <Text font="regular14" color="purple80" text={t('limitedDescription')} />
      <Separator />
      {appFollowers.length === 0 ? (
        <Card variant="gray" space={6}>
          <Stack space={4} orientation="horizontal">
            <IconPersonLimited color="purple80" />
            <Text text={t('noUsersLimited')} color="purple80" />
          </Stack>
        </Card>
      ) : (
        <Stack space={2}>
          {appFollowers.map((follower) => (
            <UserCard
              key={follower.id}
              {...follower}
              departments={follower.departments?.map(
                (department) =>
                  getTranslatedTitle(department.translations, lang, current.defaultLanguage) ?? '',
              )}
              actions={
                <>
                  <BadgeCounter value={follower.numApplications ?? 0} size="sm" />
                  {hasManageAccess && (
                    <>
                      <IconButton
                        variant="ghost-purple"
                        icon={<IconEdit />}
                        onClick={() => editCandidateAccess(follower)}
                        disabled={!job.hasApplications}
                        tooltip={job.hasApplications ? t('editCandidateAccess') : t('noApplicants')}
                      />
                      <IconButton
                        variant="ghost-purple"
                        icon={<IconRemove />}
                        onClick={() => removeAppFollower(follower.id)}
                        tooltip={t('removeFromLimited')}
                      />
                    </>
                  )}
                </>
              }
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

const accessLocale = {
  en: {
    sidebarHeaderHiringTeam: 'Add to hiring team',
    sidebarHeaderLimitedAccess: 'Add to limited access',
    hiringTeam: 'Hiring team',
    description:
      'Members of the hiring team get access to all applications in the job and can manage most job settings',
    addToTeam: 'Add to hiring team',
    noUsersHiring: 'No users in hiring team',
    noteHiring:
      'There are no members in the hiring team only super admins are able to see and manage this job but will not receive any notifications. Add yourself or anyone to the team to receive relevant notifications for the job. ',
    limitedAccess: 'Limited access',
    limitedDescription:
      'Users with limited access can only see and work with applications they have been manually added to',
    grantAccess: 'Add to limited access',
    noUsersLimited: 'No users with limited access on this job',
    removeFromHiring: 'Remove from hiring team',
    removeFromLimited: 'Remove from limited access',
    noApplicants: 'There are no applicants on this job',
    editCandidateAccess: 'Edit candidate access',
  },
  is: {
    sidebarHeaderHiringTeam: 'Bæta við ráðningarteymi',
    sidebarHeaderLimitedAccess: 'Bæta við takmarkaðan aðgang',
    hiringTeam: 'Ráðningarteymi',
    description:
      'Starfsmenn fá aðgang að öllum umsóknum og geta stjórnað flestum stillingum innan starfsins',

    addToTeam: 'Bæta við ráðningarteymi',
    noUsersHiring: 'Engir notendur í ráðningarteymi',
    noteHiring:
      'Það eru engir notendur í ráðningarteymi, aðeins ofur-notendur geta séð og stjórnað þessu starfi en munu ekki fá neinar tilkynningar. Sem ofur-notandi getur þú bætt þér við til að fá viðeigandi tilkynningar.',
    limitedAccess: 'Takmarkaður aðgangur',
    limitedDescription:
      'Takmarkaðir notendur geta aðeins fengið aðgang að umsóknum þar sem þeim hefur verið bætt við handvirkt af öðrum notanda',
    grantAccess: 'Bæta við takmarkaðan aðgang',
    noUsersLimited: 'Engir notendur eru takmarkaðan aðgang á þessu starfi',
    removeFromHiring: 'Fjarlægja úr ráðningarteymi',
    removeFromLimited: 'Fjarlægja úr takmarkaður aðgangur',
    noApplicants: 'Það eru engar umsóknir á þessi starfi',
    editCandidateAccess: 'Uppfæra aðgang að umsóknum',
  },
};
